import { Button, ButtonGroup, Chip, EditIcon, Modal, Row, Table, TableColumns } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { useColumnsSort } from '../../hooks/useColumnsSort';
import { useHiddenColumns } from '../../hooks/useHiddenColumns';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useLoadBasicData } from '../../hooks/useLoadBasicData';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../hooks/useSelectedSchoolYear';
import {
  use_SchoolYearsQuery,
  use_SubjectContainersQuery,
  useCreateBackendTeachingBlockVersionMutation,
  useTeachingBlockCurrentStatusQuery,
} from '../../types/planung-graphql-client-defs';
import { showSuccessCreateToast, showUserErrorToast } from '../../utils/toast';
import { TeachingBlockOverviewModal } from '../TeachingBlockVersion/TeachingBlockVersionsOverview/TeachingBlockOverviewModal';
import { TeachingBlockForm } from './Forms/TeachingBlockForm';
import { useTeachingBlocks } from './hooks/useTeachingBlocks';

type TeachingBlockStatusToken = 'current' | 'past' | 'future' | 'none';

type TeachingBlockStatusEntry = { token: TeachingBlockStatusToken; label: string };

type TeachingBlocksTableType = {
  subjectContainerName: string;
  subjectContainerUuid: string;
  versionsCount: number;
  schoolYear: string;
  grades: string;
  gradeGroups: string;
  updatedAt: string;
  description: string;
  draft: boolean;
  uuid: string;
  comment: string;
  status: string;
  statusEntry: TeachingBlockStatusEntry | null;
};

const STATUS_CHIP_STYLE: Record<TeachingBlockStatusToken, { bgColor: string; color: string }> = {
  current: { bgColor: 'var(--color-success)', color: 'var(--color-white)' },
  past: { bgColor: 'var(--color-grey-lighter)', color: 'var(--color-grey-dark)' },
  future: { bgColor: 'var(--color-primary-light)', color: 'var(--color-grey)' },
  none: { bgColor: 'var(--color-grey-lightest)', color: 'var(--color-grey)' },
};

export const TeachingBlocksTable = observer(() => {
  const { pimAuthClaims } = useAuthClaims();
  const permissions = usePermissionChecker();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const currentSchoolYear = useSelectedSchoolYear();

  const { columnVisibility, saveColumnVisibility } = useHiddenColumns('teachingBlocksTable', {
    gradeGroups: false,
  });
  const { sorting, saveSorting } = useColumnsSort('teachingBlocksTable');

  const [{ data: schoolYearData }] = use_SchoolYearsQuery();

  const [activeLoading, setActiveLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [currentSubjectContainerUuid, setCurrentSubjectContainerUuid] = useState<string | null>(null);

  const [showVersionModal, setShowVersionModal] = useState(false);

  const versionCreateContext = useMemo(() => {
    return {
      additionalTypenames: ['TeachingBlockVersion', 'TeachingBlock', 'SubjectContainer'],
    };
  }, []);

  const [{ data: subjectContainersData }] = use_SubjectContainersQuery({});

  const filteredSubjectContainersData = useMemo(() => {
    return subjectContainersData?.subjectContainers.filter((sc) => {
      const scStartSchoolYear = schoolYearData?.schoolYears.find((sy) => sy.uuid === sc.validFrom?.uuid);
      const scEndSchoolYear = schoolYearData?.schoolYears.find((sy) => sy.uuid === sc.validUntil?.uuid);

      if (!scEndSchoolYear && scStartSchoolYear && currentSchoolYear) {
        return currentSchoolYear?.start >= scStartSchoolYear?.start;
      }

      return currentSchoolYear?.start && currentSchoolYear?.end && scStartSchoolYear && scEndSchoolYear
        ? currentSchoolYear.start >= scStartSchoolYear.start && currentSchoolYear.end <= scEndSchoolYear.end
        : false;
    });
  }, [subjectContainersData?.subjectContainers, schoolYearData?.schoolYears, currentSchoolYear]);

  const { teachingBlocks } = useTeachingBlocks(currentSchoolYear?.uuid);

  const [{ data: teachingBlockCurrentStatusData }] = useTeachingBlockCurrentStatusQuery({
    variables: {
      teachingBlockUuids: teachingBlocks?.map((tb) => tb.uuid) ?? [],
    },
    requestPolicy: 'network-only',
  });

  const [, createTeachingBlockVersionMutation] = useCreateBackendTeachingBlockVersionMutation();

  const { gradeGroupData } = useLoadBasicData({ pause: !teachingBlocks });

  const teachingBlockStatusByUuid = useMemo(() => {
    return new Map(
      (teachingBlockCurrentStatusData?.teachingBlockCurrentStatus ?? []).map((status) => [
        status.teachingBlockUuid,
        status,
      ]),
    );
  }, [teachingBlockCurrentStatusData?.teachingBlockCurrentStatus]);

  const getStatusEntry = useCallback(
    (teachingBlockUuid: string, hasVersion: boolean, hasActiveVersion: boolean): TeachingBlockStatusEntry | null => {
      if (!hasVersion) {
        return null;
      }

      const status = teachingBlockStatusByUuid.get(teachingBlockUuid);

      if (!status || (!status.isCurrent && !status.currentFrom)) {
        return { token: 'none', label: t('timetable.status.none') };
      }

      if (status.isCurrent) {
        return hasActiveVersion
          ? { token: 'current', label: t('timetable.status.current') }
          : { token: 'none', label: t('timetable.status.none') };
      }

      if (status.currentFrom) {
        if (!hasActiveVersion) {
          return { token: 'none', label: t('timetable.status.none') };
        }
        // currentFrom liegt in der Vergangenheit (und ist nicht aktuell) -> bereits vergangen
        if (dayjs(status.currentFrom).isBefore(dayjs(), 'day')) {
          return { token: 'past', label: t('timetable.status.past') };
        }
        return {
          token: 'future',
          label: t('timetable.status.future', { date: dayjs(status.currentFrom).format('DD.MM.YYYY') }),
        };
      }

      return { token: 'past', label: t('timetable.status.past') };
    },
    [teachingBlockStatusByUuid, t],
  );

  const memoizedData = useMemo(
    (): TeachingBlocksTableType[] =>
      filteredSubjectContainersData?.map((sc) => {
        const teachingBlock = teachingBlocks?.find((tb) => tb.subjectContainerUuid === sc.uuid);
        const versionsCount = teachingBlock?.versions.filter((version) => !version.isSnapshot).length ?? 0;
        const hasVersion = versionsCount > 0;
        const hasActiveVersion = teachingBlock?.versions.some((version) => version.active) ?? false;
        const statusEntry = teachingBlock?.uuid
          ? getStatusEntry(teachingBlock.uuid, hasVersion, hasActiveVersion)
          : getStatusEntry('', false, false);

        return {
          subjectContainerName: sc.name,
          subjectContainerUuid: sc.uuid,
          versionsCount,
          updatedAt: teachingBlock?.updatedAt ? dayjs(teachingBlock?.updatedAt).format('DD.MM.YYYY - HH:mm') : '-',
          description: teachingBlock?.comment ?? '',
          draft: teachingBlock?.draft ?? true,
          uuid: teachingBlock?.uuid ?? '',
          comment: teachingBlock?.comment ?? '',
          status: statusEntry?.label ?? '',
          statusEntry,
          schoolYear: currentSchoolYear?.shortName ?? '',
          grades: sc?.grades.sort((a, b) => a - b).join(', ') ?? '',
          gradeGroups:
            sc?.gradeGroups
              .map((gg) => {
                return gradeGroupData?.gradeGroups.find((g) => g.uuid === gg.uuid)?.shortName;
              })
              .sort()
              .join(', ') ?? '',
        };
      }) ?? [],
    [
      filteredSubjectContainersData,
      getStatusEntry,
      teachingBlocks,
      currentSchoolYear?.shortName,
      gradeGroupData?.gradeGroups,
    ],
  );

  const tableColumns: TableColumns<TeachingBlocksTableType>[] = useMemo(() => {
    return [
      {
        header: t('subjectContainer.title', { count: 1 }),
        accessorKey: 'subjectContainerName',
        id: 'subjectContainerName',
        size: 200,
        canExpand: true,
      },
      {
        header: t('common.schoolYear'),
        accessorKey: 'schoolYear',
        id: 'schoolYear',
        size: 100,
        canExpand: true,
      },
      {
        header: t('grades.title', { count: 2 }),
        accessorKey: 'grades',
        id: 'grades',
        size: 100,
        canExpand: true,
      },
      {
        header: t('gradeGroups.title', { count: 2 }),
        accessorKey: 'gradeGroups',
        id: 'gradeGroups',
        size: 100,
        canExpand: true,
      },
      {
        header: t('common.comment'),
        id: 'description',
        accessorKey: 'description',
        size: 100,
        canExpand: true,
      },
      {
        header: t('common.status'),
        id: 'status',
        accessorKey: 'status',
        size: 160,
        cell: ({ row }) =>
          row.original.statusEntry ? (
            <Chip
              value={row.original.statusEntry.label}
              bgColor={STATUS_CHIP_STYLE[row.original.statusEntry.token].bgColor}
              color={STATUS_CHIP_STYLE[row.original.statusEntry.token].color}
            />
          ) : null,
      },
      {
        header: t('timetableDraft.versionCount'),
        id: 'versionsCount',
        accessorKey: 'versionsCount',
        size: 100,
      },
      {
        header: t('common.updatedAt'),
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        type: 'date',
        size: 150,
      },
    ];
  }, [t]);

  const handleEdit = (row: Row<TeachingBlocksTableType>) => {
    setCurrentSubjectContainerUuid(row.original.subjectContainerUuid);
    setIsModalOpen(true);
  };

  function closeModal() {
    setCurrentSubjectContainerUuid(null);
    setIsModalOpen(false);
  }

  function handleShowVersionModal(row: Row<TeachingBlocksTableType>) {
    setCurrentSubjectContainerUuid(row.original.subjectContainerUuid);
    setShowVersionModal(true);
  }

  function handleHideVersionModal() {
    setCurrentSubjectContainerUuid(null);
    setShowVersionModal(false);
  }

  async function onVersionAdd({ subjectContainerUuid }: { subjectContainerUuid: string }) {
    setActiveLoading(true);
    const response = await createTeachingBlockVersionMutation(
      {
        organizationUuid: pimAuthClaims.getOrganizationUuid(),
        schoolYearUuid: currentSchoolYear?.uuid ?? '',
        subjectContainerUuid: subjectContainerUuid,
      },
      versionCreateContext,
    );
    response.error ? showUserErrorToast({ error: response.error }) : showSuccessCreateToast();
    setActiveLoading(false);
  }

  return (
    <>
      <Table<TeachingBlocksTableType>
        showBorderRadius
        showShadow
        // mobil Karten wie bei den Stundenplänen: Aktionen direkt am Eintrag (mobile-plan.md E14/E24)
        isDesktop={!isMobile}
        showVisibility
        showSort={true}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={saveColumnVisibility}
        sorting={sorting}
        onSortingChange={saveSorting}
        columns={tableColumns}
        data={memoizedData}
        showActionBar
        actionBarSettings={{ showAddButton: permissions?.canWriteTimetable() }}
        isOnWhite={false}
        printerSettings={{
          headline: pimAuthClaims.getProfile()?.organization.name,
          subline: `${t('teachingBlock.title', { count: 2 })} - ${t('common.schoolYear')} ${currentSchoolYear?.shortName}`,
          filename: `${t('teachingBlock.title', { count: 2 })}_${currentSchoolYear?.shortName}`,
        }}
        lastColWidth='160px'
        lastCol={(row) => {
          if (row.original.versionsCount === 0) {
            return (
              <Button
                disabled={!permissions?.canWriteTimetable()}
                isLoading={activeLoading}
                onClick={() => onVersionAdd({ subjectContainerUuid: row.original.subjectContainerUuid })}
                hierarchy='secondary'
              >
                {t('teachingBlock.add')}
              </Button>
            );
          }

          return (
            <ButtonGroup className='ml-4'>
              <Button hierarchy={'secondary'} onClick={() => handleShowVersionModal(row)}>
                {t('pinboard.versions')}
              </Button>
              <Button
                hierarchy={'secondary'}
                disabled={!permissions?.canWriteTimetable() || row.original.versionsCount === 0}
                onClick={() => {
                  handleEdit(row);
                }}
                icon={<EditIcon className='small' />}
              />
            </ButtonGroup>
          );
        }}
      />

      <Modal isOpen={isModalOpen} onRequestClose={closeModal} title={t('teachingBlock.edit')}>
        {currentSubjectContainerUuid && (
          <TeachingBlockForm onClose={closeModal} currentSubjectContainerUuid={currentSubjectContainerUuid} />
        )}
      </Modal>

      {currentSubjectContainerUuid && (
        <TeachingBlockOverviewModal
          currentSubjectContainerUuid={currentSubjectContainerUuid}
          isOpen={showVersionModal}
          onClose={() => handleHideVersionModal()}
        />
      )}
    </>
  );
});
